import { useGlobalStore } from "@/stores/global";

export const adjust = (value: number | undefined, delta: number) =>
  (value || 0) + delta;

/**
 * `adjust` for a count that can't be negative. A dropped decrement is a number
 * that's one too high until the next refetch; an unclamped one renders "-1" in
 * a badge, so every optimistic bucket write goes through this.
 */
export const adjustCount = (value: number | undefined, delta: number) =>
  Math.max(0, adjust(value, delta));

export const isNumericString = (value: string) => {
  return !isNaN(Number(value));
};

export const formatPrice = (price: string | number, currency?: string) => {
  const numPrice = typeof price === "string" ? parseFloat(price) : price;

  // Use provided currency, or fall back to store's organization currency, or default to USD
  const currencyCode =
    currency ?? useGlobalStore.getState().organization?.currency ?? "USD";

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currencyCode,
  }).format(numPrice);
};

export interface PriceParts {
  currency: string;
  amount: string;
}

/** `formatPrice` split into its currency and its figure, for a headline that sets the currency smaller. */
export const priceParts = (price: number, currency?: string): PriceParts => {
  const currencyCode =
    currency ?? useGlobalStore.getState().organization?.currency ?? "USD";
  const parts = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currencyCode,
  }).formatToParts(price);
  const join = (isCurrency: boolean) =>
    parts
      .filter((part) => (part.type === "currency") === isCurrency)
      .map((part) => part.value)
      .join("")
      .trim();

  return { currency: join(true), amount: join(false) };
};

/** A headline amount: whole units only, so a hero figure does not carry ".00". */
export const formatWholePrice = (price: string | number, currency?: string) => {
  const numPrice = typeof price === "string" ? parseFloat(price) : price;
  const currencyCode =
    currency ?? useGlobalStore.getState().organization?.currency ?? "USD";

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currencyCode,
    maximumFractionDigits: 0,
  }).format(numPrice);
};

export const formatCompactPrice = (
  price: string | number,
  currency?: string,
) => {
  const numPrice = typeof price === "string" ? parseFloat(price) : price;

  const currencyCode =
    currency ?? useGlobalStore.getState().organization?.currency ?? "USD";

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currencyCode,
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(numPrice);
};

// Currency formatter that keeps sub-unit precision (e.g. fractional-cent per-message
// rates), unlike formatPrice which rounds to the currency's default 2 decimals.
export const formatUnitPrice = (
  value: string | number,
  currency?: string,
  maximumFractionDigits = 4,
) => {
  const numValue = typeof value === "string" ? parseFloat(value) : value;

  const currencyCode =
    currency ?? useGlobalStore.getState().organization?.currency ?? "USD";

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currencyCode,
    maximumFractionDigits,
  }).format(numValue);
};

export const getCurrencySymbol = (currency?: string | null) => {
  const currencyCode =
    currency ?? useGlobalStore.getState().organization?.currency ?? "USD";

  const symbol = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currencyCode,
  })
    .formatToParts(0)
    .find((part) => part.type === "currency")?.value;

  return symbol ?? currencyCode;
};

/**
 * `NumberField` format options for a money input - narrow symbol so the field
 * stays compact. One definition so every money field renders the same way.
 */
export const currencyFieldFormatOptions = (
  currency: string | null | undefined,
): Intl.NumberFormatOptions => ({
  style: "currency",
  currency: currency ?? "USD",
  currencyDisplay: "narrowSymbol",
});

export const formatNumber = (price: string | number) => {
  const numPrice = typeof price === "string" ? parseFloat(price) : price;

  return new Intl.NumberFormat("en-US").format(numPrice);
};

/**
 * Counts that only need a shape, not a value - follower totals, message volumes.
 * `formatNumber` is still the right call anywhere the exact figure matters.
 */
export const formatCompactNumber = (value: number): string =>
  new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);

export const formatMs = (ms: number): string => {
  if (ms < 1000) return `${ms}ms`;

  return `${(ms / 1000).toFixed(1)}s`;
};

/** Whole-percent move from `previous` to `current`; null when there is nothing to compare against. */
export const percentChange = (
  current: number | null,
  previous: number | null,
): number | null =>
  current !== null && previous !== null && previous > 0
    ? Math.round(((current - previous) / previous) * 100)
    : null;

/** A figure kept to one decimal, as a number: 42.46 is 42.5. */
export const roundToTenth = (value: number) => Math.round(value * 10) / 10;
